import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { cn } from "../utils";
import { RequiredMark } from "../required-mark";
import type { RadioGroupProps } from "./radio.types";

/**
 * RadioGroup Component
 *
 * V&A Waterfront Design System - Radio Group
 *
 * A group of radio options with a group label, helper text, and error states.
 * Uses hidden native radio inputs with custom styled visuals for consistent appearance.
 *
 * @example
 * // Basic usage
 * <RadioGroup label="Size" options={[{ label: "Small", value: "sm" }, { label: "Large", value: "lg" }]} />
 *
 * // With error
 * <RadioGroup label="Size" options={options} errorText="Please select a size" />
 *
 * // On dark background
 * <RadioGroup label="Size" options={options} onDark />
 *
 * // Small size
 * <RadioGroup label="Size" options={options} size="sm" />
 */
const RadioGroup = React.forwardRef<HTMLFieldSetElement, RadioGroupProps>((t0, ref) => {
  const $ = _c(50);
  const {
    label,
    helperText,
    errorText,
    onDark: t1,
    className,
    disabled,
    id,
    name,
    options,
    value,
    onChange,
    onBlur,
    required,
    size: t2
  } = t0;
  const onDark = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "md" : t2;
  const generatedId = React.useId();
  const groupId = id || generatedId;
  const descriptionId = `${groupId}-description`;
  const groupName = name || groupId;
  const hasError = Boolean(errorText);
  const hasDescription = Boolean(errorText || helperText);
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = React.useState(value ?? "");
  const selectedValue = isControlled ? value : internalValue;
  let t3;
  if ($[0] !== isControlled || $[1] !== onChange) {
    t3 = optionValue => {
      if (!isControlled) {
        setInternalValue(optionValue);
      }
      onChange?.(optionValue);
    };
    $[0] = isControlled;
    $[1] = onChange;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleChange = t3;
  const isMd = size === "md";
  const t4 = onDark && "dark";
  let t5;
  if ($[3] !== className || $[4] !== t4) {
    t5 = cn("group/radio flex w-full flex-col gap-2", t4, className);
    $[3] = className;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = hasDescription ? descriptionId : undefined;
  const t7 = hasError || undefined;
  const t8 = disabled ? "text-input-text-disabled" : "text-input-text-label";
  let t9;
  if ($[6] !== t8) {
    t9 = cn("mb-2 typography-component-sm-medium desktop:typography-component-md-medium", t8);
    $[6] = t8;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== required) {
    t10 = <RequiredMark required={required} />;
    $[8] = required;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== label || $[11] !== t10 || $[12] !== t9) {
    t11 = <legend className={t9}>{label}{t10}</legend>;
    $[10] = label;
    $[11] = t10;
    $[12] = t9;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== disabled || $[15] !== groupId || $[16] !== groupName || $[17] !== handleChange || $[18] !== isMd || $[19] !== onBlur || $[20] !== options || $[21] !== required || $[22] !== selectedValue) {
    let t13;
    if ($[24] !== disabled || $[25] !== groupId || $[26] !== groupName || $[27] !== handleChange || $[28] !== isMd || $[29] !== onBlur || $[30] !== required || $[31] !== selectedValue) {
      t13 = option => {
        const optionId = `${groupId}-${option.value}`;
        const isSelected = selectedValue === option.value;
        return <label key={option.value} htmlFor={optionId} className={cn("inline-flex cursor-pointer items-center gap-3", disabled && "pointer-events-none")}><span className="relative inline-flex shrink-0"><input type="radio" id={optionId} name={groupName} value={option.value} checked={isSelected} onChange={() => handleChange(option.value)} disabled={disabled} required={required} onBlur={onBlur} className="peer sr-only" /><span className={cn("flex items-center justify-center rounded-full border-[1.5px] transition-colors", isMd ? "size-5" : "size-4", "border-[#e2e8f0] bg-white", !isSelected && "hover:border-[#204795]", isSelected && "border-[#204795] bg-[#204795]", isSelected && "hover:bg-[#1a3a7a] hover:border-[#1a3a7a]", "peer-focus-visible:shadow-[0_0_0_2px_#204795]", disabled && "bg-[#f1f5f9] border-[#e2e8f0] opacity-50", disabled && isSelected && "bg-[#94a3b8] border-[#94a3b8] opacity-50", "[.dark_&]:bg-[#002b4c] [.dark_&]:border-[#334155]", !isSelected && "[.dark_&]:hover:border-[#204795]", isSelected && "[.dark_&]:bg-[#204795] [.dark_&]:border-[#204795]", isSelected && "[.dark_&]:hover:bg-[#1a3a7a] [.dark_&]:hover:border-[#1a3a7a]", "[.dark_&]:peer-focus-visible:shadow-[0_0_0_2px_#1e40af]", disabled && "[.dark_&]:bg-[#1e293b] [.dark_&]:border-[#334155]")} aria-hidden="true">{isSelected && <span className={cn("rounded-full bg-white", isMd ? "size-2" : "size-1.5")} />}</span></span><span className={cn("typography-body-sm-regular text-text-primary", disabled && "text-input-text-disabled")}>{option.label}</span></label>;
      };
      $[24] = disabled;
      $[25] = groupId;
      $[26] = groupName;
      $[27] = handleChange;
      $[28] = isMd;
      $[29] = onBlur;
      $[30] = required;
      $[31] = selectedValue;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    t12 = options.map(t13);
    $[14] = disabled;
    $[15] = groupId;
    $[16] = groupName;
    $[17] = handleChange;
    $[18] = isMd;
    $[19] = onBlur;
    $[20] = options;
    $[21] = required;
    $[22] = selectedValue;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[33] !== t12) {
    t13 = <div className="flex flex-col gap-3" role="radiogroup">{t12}</div>;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== descriptionId || $[36] !== errorText || $[37] !== hasDescription || $[38] !== hasError || $[39] !== helperText) {
    t14 = hasDescription && <p id={descriptionId} role={hasError ? "alert" : undefined} className={cn("typography-component-sm-regular desktop:typography-component-md-regular", hasError ? "text-input-text-error" : "text-input-text-label")}>{errorText || helperText}</p>;
    $[35] = descriptionId;
    $[36] = errorText;
    $[37] = hasDescription;
    $[38] = hasError;
    $[39] = helperText;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== disabled || $[42] !== ref || $[43] !== t11 || $[44] !== t13 || $[45] !== t14 || $[46] !== t5 || $[47] !== t6 || $[48] !== t7) {
    t15 = <fieldset ref={ref} data-testid="radio-group" className={t5} aria-describedby={t6} aria-invalid={t7} disabled={disabled}>{t11}{t13}{t14}</fieldset>;
    $[41] = disabled;
    $[42] = ref;
    $[43] = t11;
    $[44] = t13;
    $[45] = t14;
    $[46] = t5;
    $[47] = t6;
    $[48] = t7;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  return t15;
});
RadioGroup.displayName = "RadioGroup";
export { RadioGroup };